import logger from '../utils/logger';

/**
 * The name of the window global variable for new relic.
 * @type {string}
 */
export const NEW_RELIC_GLOBAL = 'newrelic';
/**
 * New Relic Insights lookup for any application using dotcom-nav-v3.
 * @type {string}
 */
export const ACTION_NAME = 'dotcom-nav-v3';

/**
 * Return the new relic global.
 * @returns {*}
 */
export function getNewRelic() {
  return typeof window !== 'undefined' && window[NEW_RELIC_GLOBAL];
}

/**
 * Create a newrelic appData.
 * @param appData {NikeDotcomNav}
 * @returns {Object}
 */
export default function initNewRelic(appData) {
  // Include navVersion as an attribute in PageView table so we can compare page performance with v2
  // https://docs.newrelic.com/docs/browser/new-relic-browser/browser-apis/setcustomattribute/
  getNewRelic()?.setCustomAttribute?.('navVersion', 'v3');

  /**
   * Sends PageAction to New Relic Browser if globally available
   * @param details {Object}
   * @param actionNameExtension {String}
   * @returns {Boolean} whether attempted to send to New Relic Browser because globally available
   */
  function trigger(details, actionNameExtension) {
    const { env } = appData;
    // requires applications to implement New Relic Browser
    if (!getNewRelic()) {
      if (env !== 'prod') {
        logger.warn('Global newrelic instance is inaccessible');
      }
      return false;
    }
    const finalActionName = actionNameExtension
      ? `${ACTION_NAME}-${actionNameExtension}`
      : ACTION_NAME;
    getNewRelic().addPageAction(finalActionName, details);
    return true;
  }

  /**
   * Builds PageAction payload and fires New Relic Browser trigger
   * This project has typically used the details alone in the past
   * Action name is the typical way of sending an event to New Relic at Nike
   * Now we have actionNameExtension as an optional parameter to maintain backwards compatibility
   * @param {Object} details
   * @param {String} actionNameExtension
   * @returns {Boolean} whether fired trigger
   */
  function log(details, actionNameExtension) {
    const { id, timestamp, version } = appData.generation || {};

    return trigger(
      {
        generationId: id,
        generationTimestamp: timestamp,
        generationVersion: version,
        ...details,
      },
      actionNameExtension,
    );
  }

  /**
   * Builds error specific PageAction payload
   * @param context {String} Custom description
   * @param stack {String} Error.stack
   * @param message {String} Error.message
   * @returns {Boolean} whether fired trigger
   */
  function error({ stack, message }, context = '') {
    return log({
      context,
      errorMessage: message,
      errorStack: stack,
      type: 'error',
    });
  }

  /**
   * Builds error specific PageAction payload
   * @param context {String} Custom description
   * @returns {Function} stopTrace
   */
  function startTrace(context = '') {
    const { startTime: startTraceTime } = window.performance.mark(`${context}Start`);

    function stopTrace() {
      const { startTime: stopTraceTime } = window.performance.mark(`${context}End`);
      const { duration, entryType } = window.performance.measure(
        `${context}Measure`,
        `${context}Start`,
        `${context}End`,
      );

      return log({
        context,
        entryType,
        duration,
        startTraceTime,
        stopTraceTime,
        type: 'trace',
      });
    }
    return stopTrace;
  }

  /**
   * Sends page performance metrics to New relic on page load
   */
  function initPerformanceMetrics() {
    window.addEventListener('load', () => {
      const navigation = window.performance?.getEntriesByType?.('navigation')[0];
      if (navigation) {
        // https://developer.mozilla.org/en-US/docs/Web/API/PerformanceNavigationTiming/domInteractive
        // same as domContentLoadedEventStart
        log({
          context: 'v3PageDomInteractive',
          duration: navigation.domInteractive,
          entryType: navigation.entryType,
        });

        //https://developer.mozilla.org/en-US/docs/Web/API/PerformanceNavigationTiming/loadEventStart
        log({
          context: 'v3PageLoad',
          duration: navigation.loadEventStart,
          entryType: navigation.entryType,
        });
      }
    });
  }

  return {
    error,
    initPerformanceMetrics,
    log,
    startTrace,
    trigger,
  };
}
